import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActionModel } from '../action.model';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { UtilsService } from 'src/app/shared/utils.service';
import { ActionsNotesEditComponent } from '../actions-notes-edit/actions-notes-edit.component';
import { tap } from 'rxjs/operators';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-actions-notes',
  templateUrl: './actions-notes.component.html',
  styleUrls: ['./actions-notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActionsNotesComponent implements OnInit {
  @Input() action: ActionModel;
  @Input() is_edit!: boolean;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource = new ActionsNotesDataSource(this.app, this.api);

  displayedColumns = [
    'id',
    'note',
    'date_created'
    // 'date_created_UTC',
  ];

  canAccess: boolean = false;
  canEdit: boolean = false;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    private utils: UtilsService,
    public rbac: RBACService
  ) {}



  ngOnInit() {
    // A user managing their own task can read and write its notes without the account level Tasks
    // permission, matching the API. This component is shared between the Tasks register and the
    // personal My Tasks dialog, and in the personal case the user may hold no feature permissions
    // at all, so ownership of the task is what grants access there.
    const ownsTask: boolean = ActionModel.isOwnedBy(this.action, this.app.user?.id);

    this.canAccess =
      ownsTask ||
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canEdit =
      ownsTask ||
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      );

    if (!this.canAccess) {
      return;
    }

    if (this.canEdit) {
      this.displayedColumns.push('buttons');
    }

    this.dataSource.actionId = this.action.id;
    this.dataSource.getData(true);
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by their corresponding date columns.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData(false); // TBD: Need to add sorting to the API endpoint.
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onEdit(noteId: number = null) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit task notes.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ActionsNotesEditComponent,
      {
        action: this.action,
        noteId: noteId
      },
      {},
      () => {
        this.dataSource.getData(true);
      }
    );
  }

  onDelete(noteId: number) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete task notes.'
      );
      return;
    }
    this.utils.showModal(
      'Delete Note',
      'Are you sure you want to delete the note?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v1/actions/${this.action.id}/notes/${noteId}`,
            {},
            {},
            {}
          )
          .then((resposne: any) => {
            this.utils.showToast('Note Deleted.');
            this.dataSource.getData();
          });
      }
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class ActionsNotesDataSource extends CustomDataSource {
  actionId: number;

  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset = false) {
    this.getDataFromLaravelAPI(`actions/${this.actionId}/notes`, resetOffset);
  }
}
